/**
 * 模拟交易API服务
 *
 * @author page
 * @date 2026-04-21
 */
import {api} from './api';
import type {
    ExchangeConfig,
    KlineData,
    KlineWithIndicatorsResponse,
    SimulateAccount,
    SimulateLedger,
    SimulateOrder,
    SimulatePosition,
    SimulateTrade,
    SymbolConfigRecord,
} from '../types/simulate';

export const simulateTradingService = {
    /**
     * 初始化模拟账户（新引擎）
     */
    initAccount: (initialBalance: string, mode = 0) =>
        api.post('/api/v1/simulate/account/init', null, {params: {initialBalance, mode}}),

    /**
     * 获取账户信息（新引擎）
     */
    getAccount: () =>
        api.get<ApiResponse<SimulateAccount>>('/api/v1/simulate/account'),

    getKline: (pair: string, timeframe: string, limit = 100, productType?: string) =>
        api.get<ApiResponse<KlineData[]>>('/api/v1/simulate/kline', {
            params: {pair, timeframe, limit, ...(productType && {productType})},
        }),

    /**
     * 获取K线数据和技术指标
     * @param pair 交易对
     * @param timeframe 时间周期
     * @param limit 数据条数
     * @param indicators 指标列表，逗号分隔
     * @param productType 产品类型
     * @param periodParams 可选的指标周期参数配置
     */
    getKlineWithIndicators: (pair: string, timeframe: string, limit = 100, indicators?: string, productType?: string, periodParams?: {
        emaPeriods?: number[];
        smaPeriods?: number[];
        rsiPeriods?: number[];
        bollPeriods?: number[];
        bollStdDevs?: number[];
        kdjPeriods?: number[];
        cciPeriods?: number[];
        atrPeriods?: number[];
        obvPeriods?: number[];
        adxPeriods?: number[];
        macdParams?: string;
    }) => {
        const params: Record<string, any> = {pair, timeframe, limit};
        if (indicators) params.indicators = indicators;
        if (productType) params.productType = productType;
        if (periodParams) {
            Object.entries(periodParams).forEach(([key, value]) => {
                if (null !== value && undefined !== value) {
                    params[key] = value;
                }
            });
        }
        return api.get<ApiResponse<KlineWithIndicatorsResponse>>('/api/v1/simulate/kline-with-indicators', {params});
    },

    /**
     * 更新账户配置（新引擎）
     */
    updateAccount: (data: Partial<SimulateAccount>) =>
        api.post<ApiResponse<SimulateAccount>>('/api/v1/simulate/account/config', null, {params: data}),

    /**
     * 下单（新引擎）
     */
    placeOrder: (params: {
        exchange: string;
        symbol: string;
        side: number;
        type: number;
        price?: string;
        quantity: string;
        tradingMode?: number;
        leverage?: number;
    }) => api.post<ApiResponse<SimulateOrder>>('/api/v1/simulate/orders', params),

    /**
     * 撤单（新引擎）
     */
    cancelOrder: (orderId: number) =>
        api.post(`/api/v1/simulate/v2/orders/${orderId}/cancel`),

    /**
     * 查询订单列表（新引擎）
     */
    getOrders: (symbol?: string, status?: number) =>
        api.get<ApiResponse<SimulateOrder[]>>('/api/v1/simulate/orders', {
            params: {symbol, status},
        }),

    /**
     * 查询成交记录（新引擎）
     */
    getTrades: (symbol?: string) =>
        api.get<ApiResponse<SimulateTrade[]>>('/api/v1/simulate/trades', {
            params: {symbol},
        }),

    /**
     * 查询持仓（新引擎）
     */
    getPositions: () =>
        api.get<ApiResponse<SimulatePosition[]>>('/api/v1/simulate/positions'),

    /**
     * 查询资金流水（新引擎）
     */
    getLedger: (eventType?: number) =>
        api.get<ApiResponse<SimulateLedger[]>>('/api/v1/simulate/ledger', {
            params: {eventType},
        }),

    /**
     * 触发手动恢复（新引擎）
     */
    triggerRecovery: () =>
        api.post('/api/v1/simulate/recovery/trigger'),

    /**
     * 模拟充值（新引擎）
     */
    topUpAccount: (amount: number) =>
        api.post('/api/v1/simulate/account/top-up', null, {params: {amount}}),

    closePosition: (positionId: number, quantity?: number) =>
        api.post(`/api/v1/simulate/v2/positions/${positionId}/close`, null, {params: {quantity}}),

    /**
     * 获取品种实时价格
     * @param pair 交易对，如 ETH/USDT
     * @param productType 产品类型，如 spot/contract
     */
    getRealtimePrice: (pair: string, productType?: string) =>
        api.get<ApiResponse<RealTimePriceModel>>('/api/v1/simulate/realtime-price', {
            params: {pair, ...(productType && {productType})},
        }),
};

/**
 * 模拟交易所配置 API 服务
 */
export const simulateExchangeConfigService = {
    /**
     * 获取所有交易所配置
     */
    getAllExchanges: () =>
        api.get<ApiResponse<ExchangeConfig[]>>('/api/v1/simulate/exchanges'),

    /**
     * 更新单个交易所的启用状态
     */
    updateExchangeStatus: (exchangeName: string, enabled: number) =>
        api.put<ApiResponse<ExchangeConfig>>(`/api/v1/simulate/exchanges/${exchangeName}/status`, {enabled}),

    /**
     * 批量更新交易所配置
     */
    batchUpdateExchanges: (configs: Array<{ exchangeName: string; enabled?: number; sortOrder?: number }>) =>
        api.put<ApiResponse<ExchangeConfig[]>>('/api/v1/simulate/exchanges', configs),
};

/**
 * 模拟交易品种配置 API 服务
 */
export const simulateSymbolConfigService = {
    /**
     * 获取所有品种配置
     */
    getAllSymbols: () =>
        api.get<ApiResponse<SymbolConfigRecord[]>>('/api/v1/simulate/symbols'),

    /**
     * 根据交易所名称获取品种配置列表
     */
    getByExchange: (exchangeName: string) =>
        api.get<ApiResponse<SymbolConfigRecord[]>>(`/api/v1/simulate/symbols/exchange/${exchangeName}`),

    /**
     * 根据交易所名称和产品类型获取品种配置列表
     */
    getByExchangeAndProductType: (exchangeName: string, productType: string) =>
        api.get<ApiResponse<SymbolConfigRecord[]>>(`/api/v1/simulate/symbols/exchange/${exchangeName}/${productType}`),

    /**
     * 根据产品类型获取品种配置列表
     */
    getByProductType: (productType: string) =>
        api.get<ApiResponse<SymbolConfigRecord[]>>(`/api/v1/simulate/symbols/productType/${productType}`),

    /**
     * 新增品种配置
     */
    addSymbol: (data: {
        exchangeName: string;
        productType: string;
        symbol: string;
        enabled?: number;
        sortOrder?: number
    }) =>
        api.post<ApiResponse<SymbolConfigRecord>>('/api/v1/simulate/symbols', data),

    /**
     * 更新品种配置
     */
    updateSymbol: (id: number, data: {
        exchangeName: string;
        productType: string;
        symbol: string;
        enabled?: number;
        sortOrder?: number
    }) =>
        api.put<ApiResponse<SymbolConfigRecord>>(`/api/v1/simulate/symbols/${id}`, data),

    /**
     * 删除品种配置
     */
    deleteSymbol: (id: number) =>
        api.delete<ApiResponse<null>>(`/api/v1/simulate/symbols/${id}`),

    /**
     * 批量更新品种配置
     */
    batchUpdateSymbols: (configs: Array<{
        id?: number;
        exchangeName: string;
        productType: string;
        symbol: string;
        enabled?: number;
        sortOrder?: number
    }>) =>
        api.put<ApiResponse<SymbolConfigRecord[]>>('/api/v1/simulate/symbols/batch', configs),
};

/**
 * 统一响应类型，与后端 ApiResponse 结构对齐
 */
interface ApiResponse<T> {
    code: string;
    success: boolean;
    data: T;
    message?: string;
    total?: number;
}

/** 实时价格响应模型 */
interface RealTimePriceModel {
    instId: string;
    lastPrice: number;
    changePercent24H?: number;
    changePercent1H?: number;
    changePercent4H?: number;
    updateTime?: number;
}
